// /app/frontend/src/pages/seo/DatingAsACouplePage.jsx
// Focus: Co-dating logistics + couple-internal communication while dating others.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  StepCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function DatingAsACouplePage() {
  const path = '/dating-as-a-couple';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Dating As A Couple', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Dating As A Couple"
        description="Learn how couples can navigate shared dating experiences, communication, and relationship growth together."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Guide"
        headline="Dating Together As A Couple"
        sub="The relationships couples build while dating other people are often shaped less by who they meet — and more by how they talk to each other."
        body="A practical guide to dating as a couple: the conversations to have, the rhythms to keep, and the early-warning signs to listen for."
        primaryCta={{ label: 'Create A Couple Profile', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Poly Dating For Couples', to: '/poly-dating-for-couples' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12">
          <p className="text-lg text-slate-700 leading-relaxed">
            Dating as a couple is its own discipline. There is the relationship you have with each other. There is the relationship you are building with someone new. And there is the relationship between those two — the way each one feeds or starves the other. The couples who do this well treat all three as deliberate work.
          </p>
        </section>

        {/* What couples need to align on */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            What Couples Need To Align On First
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🎯"
              accent="rose"
              title="What You Are Looking For"
              body="A third long-term partner. Separate connections. Triad. Sister wife. Casual friends. Define it together, in writing, before opening up. Drift here is the most common source of trouble."
            />
            <FeatureCard
              icon="🚧"
              accent="amber"
              title="Boundaries Up Front"
              body="What is in-bounds? What is not? Physical, emotional, time, locations, sleepovers, social media. The clearer the boundaries, the less needs to be negotiated mid-date."
            />
            <FeatureCard
              icon="⏰"
              accent="sky"
              title="Time Rhythms"
              body="How many nights a week are couple-protected? How is calendar planning shared? Implicit time always becomes contested time."
            />
            <FeatureCard
              icon="💬"
              accent="emerald"
              title="Disclosure Style"
              body="How much do you share with each other about new connections? Some couples want every detail; others share less. Pick what works — but pick consciously."
            />
          </div>
        </section>

        {/* Internal communication */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            The Quiet Skill: Talking To Each Other
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              The dating outside the relationship is usually not what shifts the relationship. What shifts it is the way you talk to each other about that dating. Couples who thrive build a small set of rituals:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-1.5">
              <li>Pre-date check-ins. A brief, calm conversation before either of you goes on a date.</li>
              <li>Post-date debriefs. Same evening or next morning. Brief. No rumination at midnight.</li>
              <li>Weekly relationship check-ins. Just the two of you, no outside dating talk. The primary relationship still needs maintenance.</li>
              <li>A &ldquo;pause-and-name&rdquo; rule. Either partner can name a hard feeling without it becoming a crisis. The point is naming early, not resolving in the moment.</li>
            </ul>
          </div>
        </section>

        {/* Pacing */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Pacing The Outside Relationship
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              The single most predictable failure pattern in couple-dating is the new relationship moving faster than the existing one can absorb. New relationship energy is real, intense, and often blinds people to the impact on their existing partner.
            </p>
            <p>
              Counter it with a slow, transparent pace. Verbalize what is shifting. Continue scheduled couple time, even when the new connection is exciting. If the new person is healthy for the relationship, they will respect a slower pace; if they are not, the rush itself is the warning sign.
            </p>
          </div>
        </section>

        {/* Communication step-by-step */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            A Healthy First Few Months
          </h2>
          <div className="grid gap-3">
            <StepCard n={1} title="Align before opening" body="Have the structural conversation first — what you want, what your boundaries are, what your rhythms will be. Write it down." />
            <StepCard n={2} title="Open slowly" body="Most couples start by simply allowing flirting, then dating, then physical intimacy in stages. There is no rush to do all of it at once." />
            <StepCard n={3} title="Build the check-in habit" body="Pre-date, post-date, weekly. The discipline of small honest conversations is what protects the relationship." />
            <StepCard n={4} title="Notice the partner who is struggling" body="If one of you is consistently the one having a hard time, slow down. Real consent is not endurance." />
            <StepCard n={5} title="Renegotiate openly" body="What you agreed to in month one will need updating by month four. Renegotiation is healthy; rigid adherence to outdated agreements is not." />
          </div>
        </section>

        {/* Warning signs */}
        <section className="mb-14 rounded-2xl border border-rose-200 bg-rose-50 p-6">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-rose-900 mb-4">
            Early-Warning Signs
          </h2>
          <ul className="space-y-2 text-rose-900 list-disc list-inside">
            <li>One partner is significantly more enthusiastic than the other</li>
            <li>Date logistics start being hidden or downplayed</li>
            <li>The new connection becomes a source of comparison to the existing partner</li>
            <li>Outside-dating conversations only happen reactively, never proactively</li>
            <li>Couple time gets canceled repeatedly for outside dates</li>
            <li>One partner says &ldquo;I&rsquo;m fine&rdquo; about something that does not look fine</li>
          </ul>
          <p className="mt-4 text-rose-900 text-sm font-medium">
            None of these are deal-breakers alone. All of them deserve naming the moment you notice them.
          </p>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/poly-dating-for-couples" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Dating For Couples</p>
              <p className="text-sm text-slate-600 mt-1">Structures, features, and tools.</p>
            </a>
            <a href="/safe-poly-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Safe Poly Dating Guide</p>
              <p className="text-sm text-slate-600 mt-1">Tactical safety practices.</p>
            </a>
            <a href="/how-to-find-a-sister-wife" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">How To Find A Sister Wife</p>
              <p className="text-sm text-slate-600 mt-1">For couples building a plural family.</p>
            </a>
            <a href="/ethical-polyamory-for-beginners" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Ethical Polyamory For Beginners</p>
              <p className="text-sm text-slate-600 mt-1">Foundations of consent and care.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
